/**
 * Trame pédagogique Formation 125 (7 h) — CODE 2 ROUES.
 * Grille pédagogique enrichie 12 champs par compétence :
 *   🎯 Objectif · Pourquoi · Comment · 🏍️ Exercices · 👀 Observation ·
 *   ⚠️ Risques · 🌦️ Influences · Erreurs · 🔧 Remédiation ·
 *   🗣️ Questions · ✅ Critère de réussite · ✨ À retenir
 *
 * Contenu 100% neutre : aucune mention d'école ou d'entreprise.
 */
import { CINQ_QUESTIONS, METHODE_BASELINE } from './pedagogieCommune';

export const INTRO_125 = {
  baseline: METHODE_BASELINE,
  questions: CINQ_QUESTIONS,
  principe: "La formation 125 s'adapte au niveau réel du conducteur. La théorie peut être réduite lorsque les connaissances sont déjà maîtrisées afin de privilégier la pratique et la technique. La mise en circulation n'est pas automatique : elle intervient uniquement lorsque le niveau technique de l'élève permet de l'aborder dans des conditions de sécurité suffisantes.",
};

/* ================================================================== */
/*  PHASE 1 — THÉORIE (~1 h à 1 h 30)                                  */
/* ================================================================== */
const P1_THEORIE = [
  {
    id: 'admin-equip',
    title: 'Aspects administratifs & équipements',
    objectif: "Le conducteur connaît les conditions légales de la conduite en 125 et choisit un équipement adapté.",
    pourquoi: "Cadrer les conditions légales et rappeler pourquoi les équipements de protection ne sont pas optionnels : ils réduisent la gravité des accidents.",
    comment: ['Conditions de conduite (permis B + 7 h)', 'Conduite à l\u2019étranger', 'Équipements obligatoires (casque, gants CE)', 'Équipements complémentaires recommandés (blouson, chaussures, dorsale)', 'Vérifications de la moto avant chaque départ'],
    exercices: [
      { nom: 'Checklist équipement', niveau: 1, description: 'Le conducteur liste et justifie chaque pièce d\u2019équipement.' },
      { nom: 'Vérifications avant départ', niveau: 2, description: 'Passage en revue commandes, éclairage, freins, pneus, niveaux.' },
    ],
    observation: ['Absence de gants ou gants inadaptés', 'Casque mal ajusté', 'Vérifications superficielles ou oubliées'],
    risques: ['Rouler sans équipement adapté', 'Circuler sans les documents obligatoires', 'Défaut mécanique non détecté'],
    influences: ['Météo', 'Trajet urbain / route', 'Durée du trajet', 'Passager'],
    erreurs: ['«Le trajet est trop court pour s\u2019équiper»', 'Négliger les vérifications si moto habituelle'],
    remediation: ['Reprendre point par point avec vidéo accidentologie', 'Rendre la vérification systématique (routine 30 s)'],
    questions: ["«Quel équipement ferait la différence si tu tombais à 30 km/h en ville ?»", "«Quelle vérification n\u2019oublierais-tu jamais avant un long trajet ?»"],
    critere: 'Le conducteur s\u2019équipe systématiquement complet et effectue ses vérifications sans oubli en moins de 60 s.',
  },
  {
    id: 'vulnerabilite',
    title: 'Vulnérabilité & visibilité du 2-roues',
    objectif: "Le conducteur comprend la vulnérabilité spécifique au 2-roues et adopte les comportements qui augmentent sa visibilité.",
    pourquoi: "Le 2-roues est peu visible et physiquement exposé. Cette compréhension conditionne toute la conduite préventive.",
    comment: ['Angles morts des autres véhicules', 'Se rendre visible (position sur la voie, éclairage, vêtements)', 'Anticiper les défauts de perception des autres usagers', 'Adopter une conduite défensive'],
    exercices: [
      { nom: 'Analyse d\u2019images accidentogènes', niveau: 1, description: 'Sur 5 photos de situation, l\u2019élève identifie où le 2-roues est mal placé.' },
      { nom: 'Test angle mort', niveau: 2, description: 'Simulation en statique : où se placer pour ne PAS être dans l\u2019angle mort d\u2019un poids-lourd.' },
    ],
    observation: ['L\u2019élève ne cite pas les angles morts spontanément', 'Confusion visibilité / priorité'],
    risques: ['Ne pas être vu aux intersections', 'Se placer dans l\u2019angle mort d\u2019un camion', 'Circuler trop près des voitures'],
    influences: ['Luminosité', 'Météo', 'Densité de trafic', 'Zone (urbain / route)'],
    erreurs: ['Considérer que «l\u2019autre m\u2019a forcément vu»', 'Se placer contre le trottoir «pour ne pas gêner»'],
    remediation: ['Reprendre par vidéo casque de motard', 'Faire dessiner les angles morts sur un schéma routier'],
    questions: ["«Si tu étais l\u2019automobiliste, à quel moment aurais-tu du mal à voir la moto ?»"],
    critere: 'Le conducteur cite spontanément les 4 angles morts principaux et propose 3 actions pour rester visible.',
    aRetenir: 'Être vu, c\u2019est déjà se protéger.',
  },
  {
    id: 'accidento',
    title: 'Accidentologie & accidents-types',
    objectif: "Le conducteur identifie les 7 situations accidentogènes typiques et sait comment les anticiper.",
    pourquoi: 'Identifier les situations les plus accidentogènes permet de les anticiper avant qu\u2019elles se présentent.',
    comment: ['Intersections (perte de priorité)', 'Virages (vitesse mal évaluée)', 'Dépassements (angle mort)', 'Distance de sécurité insuffisante', 'Ouverture de portière', 'Sortie de piste', 'Freinage tardif'],
    exercices: [
      { nom: 'Étude de cas', niveau: 2, description: 'Sur 3 récits d\u2019accident, identifier les facteurs déclenchants et les comportements évitables.' },
    ],
    observation: ['Réponses générales sans analyse', 'Difficulté à hiérarchiser les causes'],
    risques: ['Collision latérale', 'Sortie de route', 'Chute par blocage de roue', 'Choc arrière'],
    influences: ['Vitesse', 'Adhérence', 'Fatigue', 'Anticipation', 'Distraction'],
    erreurs: ['Se focaliser sur l\u2019autre usager sans analyser sa propre marge', 'Sous-estimer la fatigue en trajet urbain'],
    remediation: ['Reprendre le cas problématique en le décomposant', 'Croiser avec statistiques ONISR'],
    questions: ["«Sur quel type d\u2019accident aimerais-tu qu\u2019on insiste aujourd\u2019hui ?»", "«Qu\u2019aurait pu faire ce conducteur 5 secondes avant l\u2019accident ?»"],
    critere: 'Le conducteur analyse un cas d\u2019accident en identifiant au moins 3 causes et 2 actions préventives.',
  },
  {
    id: 'stabilite-distances',
    title: 'Stabilité, distances de sécurité, intersections, virages',
    objectif: "Le conducteur comprend comment la moto se comporte en trajectoire et interagit avec les autres usagers.",
    pourquoi: 'Comprendre le comportement dynamique de la moto pour anticiper trajectoire, freinage et interactions.',
    comment: ['Stabilité liée à la vitesse et au regard', 'Distance de sécurité = 2 s minimum', 'Intersections : voir + être vu + s\u2019arrêter en sécurité', 'Virages : préparer → placer → tourner → sortir'],
    exercices: [
      { nom: 'Repère de 2 s', niveau: 2, description: 'Sur vidéo, compter «21 – 22» entre le passage d\u2019un véhicule et d\u2019un repère fixe.' },
      { nom: 'Schéma de virage', niveau: 3, description: 'Tracer la trajectoire idéale sur 3 configurations de virage.' },
    ],
    observation: ['Distances de sécurité systématiquement sous-évaluées', 'Trajectoire de virage «coupée»'],
    risques: ['Sur-régime en descente', 'Trajectoire trop intérieure', 'Freinage en virage'],
    influences: ['Vitesse d\u2019entrée', 'Rayon de virage', 'Adhérence', 'Charge'],
    erreurs: ['Rouler à 1 s au lieu de 2 s', 'Placer sa moto au milieu de la voie sans raison'],
    remediation: ['Reprendre par démonstration vidéo', 'Reprise pratique de la règle des 2 s au premier plateau'],
    questions: ["«Que se passe-t-il si tu freines dans un virage à 50 km/h ?»", "«Comment adaptes-tu ta distance sur route mouillée ?»"],
    critere: 'Le conducteur cite la règle des 2 s, décrit les 4 étapes du virage et justifie sa position dans la voie.',
  },
];

/* ================================================================== */
/*  PHASE 2 — HORS CIRCULATION (~2 h 30 à 3 h)                         */
/* ================================================================== */
const P2_HORS_CIRC = [
  {
    id: 'decouverte',
    title: 'Découverte des commandes & position',
    objectif: "L'élève identifie chaque commande, comprend son effet et adopte une position stable et mobile.",
    pourquoi: "Prendre conscience de l'emplacement et de l'effet de chaque commande, et adopter une position permettant contrôle, précision et regard.",
    comment: ['Repérer embrayage, frein avant, frein arrière, sélecteur', 'Regard loin', 'Épaules relâchées', 'Bras souples', 'Genoux au contact', 'Pieds correctement positionnés'],
    exercices: [
      { nom: 'Jeu de reconnaissance des commandes', niveau: 1, description: "Moteur coupé, l'enseignant demande une commande, l'élève la trouve et l'utilise à vide." },
      { nom: 'Position statique', niveau: 1, description: 'Vérifier chaque point d\u2019appui, tourner le guidon, déplacer le regard.' },
    ],
    observation: ['Crispation', 'Bras tendus', 'Regard trop proche', 'Confusion frein avant/embrayage au début'],
    risques: ['Actionner la mauvaise commande sous stress', 'Perte d\u2019équilibre par mauvaise position'],
    influences: ['Expérience antérieure', 'Hauteur de selle', 'Poids de la moto'],
    erreurs: ['Regarder les commandes au lieu de les sentir', 'Tenir la moto avec les bras'],
    remediation: ['Reprendre commande par commande à vide', 'Verbaliser chaque action avant de la faire'],
    questions: ["«Où sont tes points d'appui sur la moto ?»", "«Est-ce que tu tiens la moto avec tes bras ou avec tes jambes ?»"],
    critere: 'L\u2019élève identifie et utilise chaque commande sans hésitation, avec une position stable et détendue.',
  },
  {
    id: 'bequillage-deplacement',
    title: 'Béquillage & déplacement moteur arrêté',
    objectif: "L'élève manie sa moto à l'arrêt sans la faire tomber et sait la déplacer moteur coupé.",
    pourquoi: 'Compétence souvent sous-estimée mais essentielle : prévention de la chute à l\u2019arrêt et connaissance de sa machine.',
    comment: ['Sortir la moto de la béquille avec le buste', 'Trouver l\u2019équilibre statique', 'Déplacer la moto en marchant à côté', 'Reposer la moto proprement sur la béquille latérale'],
    exercices: [
      { nom: 'Béquillage avant/arrière', niveau: 1, description: 'Sortir puis reposer la moto plusieurs fois, sur sol plat.' },
      { nom: 'Déplacement à pied', niveau: 2, description: 'Avancer, reculer, effectuer une légère rotation.' },
      { nom: 'Marche arrière en pente légère', niveau: 3, description: 'Test contrôlé en présence de l\u2019enseignant.' },
    ],
    observation: ['Corps trop éloigné de la moto', 'Béquille mal engagée', 'Bras qui font tout le travail'],
    risques: ['Chute à l\u2019arrêt', 'Vouloir retenir seul une moto qui tombe (risque physique)'],
    influences: ['Poids de la moto', 'Inclinaison du sol', 'Taille du conducteur'],
    erreurs: ['Béquiller moto non stabilisée', 'Forcer avec les bras au lieu du bassin'],
    remediation: ['Décomposer chaque phase à l\u2019arrêt', 'Reprendre en zone plate avant de progresser'],
    questions: ["«Si la moto commence à tomber, que fais-tu ?»", "«Est-ce que tu peux la déplacer sur 10 m sans t'épuiser ?»"],
    critere: 'L\u2019élève béquille et déplace la moto de manière stable, y compris en légère pente, sans compensation excessive.',
  },
  {
    id: 'demarrage-arret',
    title: 'Démarrage & arrêt avec les 2 freins',
    objectif: "L'élève démarre sans à-coup, stabilise la moto, et s'arrête précisément avec les deux freins.",
    pourquoi: 'Coordonner embrayage + gaz + freins pour un contrôle progressif et sécurisé.',
    comment: ['Engager le premier rapport', 'Rechercher le point de patinage', 'Ajouter progressivement du gaz', 'Relâcher l\u2019embrayage', 'Arrêt : fermer les gaz + freins avant/arrière + débrayer'],
    exercices: [
      { nom: 'Départ → 10 m → arrêt', niveau: 1, description: 'Ligne droite courte, arrêt à un repère.' },
      { nom: 'Départ → accélération → freinage', niveau: 2, description: 'Accélération contrôlée puis arrêt précis.' },
      { nom: 'Départ → virage → arrêt', niveau: 3, description: 'Introduction d\u2019un changement de direction avant l\u2019arrêt.' },
    ],
    observation: ['Départ brutal', 'Perte d\u2019équilibre au démarrage', 'Regard qui plonge', 'Utilisation d\u2019un seul frein'],
    risques: ['Calée', 'Relâché brutal d\u2019embrayage', 'Blocage de roue à l\u2019arrêt'],
    influences: ['Type de moto', 'Charge', 'Déclivité', 'Température moteur'],
    erreurs: ['Ouvrir les gaz avant de trouver le point de patinage', 'Freiner uniquement de l\u2019arrière'],
    remediation: ['Isoler la recherche du point de patinage moto immobile', 'Rejouer le départ sans gaz (patinage seul)'],
    questions: ["«Qu'est-ce qui a provoqué ton départ trop brutal : embrayage, gaz ou les deux ?»", "«Où regardais-tu au moment de t'arrêter ?»"],
    critere: 'L\u2019élève enchaîne 5 départs/arrêts propres avec les 2 freins, en conservant équilibre et regard.',
  },
  {
    id: 'patinage-braquage',
    title: 'Patinage & braquage',
    objectif: "L'élève contrôle sa vitesse à basse allure et découvre que la moto peut tourner à faible vitesse en restant stable.",
    pourquoi: 'Compétences fondamentales pour l\u2019usage urbain quotidien : manœuvres à basse vitesse et changements de direction serrés.',
    comment: ['Doser l\u2019embrayage', 'Regard vers la sortie', 'Frein arrière pour stabiliser', 'Ne pas fixer le cône'],
    exercices: [
      { nom: 'Recherche du point de patinage', niveau: 1, description: 'Moto au ralenti, l\u2019élève cherche puis retrouve le point.' },
      { nom: 'Marche lente en ligne droite', niveau: 2, description: 'Maintenir la moto à basse allure, regard loin.' },
      { nom: 'Huit à faible allure', niveau: 3, description: 'Alterner boucles gauche et droite en réduisant l\u2019espace.' },
    ],
    observation: ['Calée récurrente', 'Guidonnage', 'Fixation du cône', 'Absence de frein arrière'],
    risques: ['Chute à faible vitesse', 'Chute intérieure par blocage de direction'],
    influences: ['Regard', 'Adhérence', 'Braquage disponible'],
    erreurs: ['Rechercher directement l\u2019allure lente sans construire le patinage', 'Regarder juste devant la roue'],
    remediation: ['Isoler la recherche du patinage moto immobile', 'Placer un point visuel de sortie', 'Reprendre le huit très large'],
    questions: ["«Qu'est-ce qui te permet d'aller lentement sans caler ?»", "«Lorsque tu regardes le cône, que fait ta trajectoire ?»"],
    critere: 'L\u2019élève réalise 10 m d\u2019allure lente stable + un huit sans poser le pied avec regard clairement porté vers la sortie.',
    aRetenir: 'La moto va là où je regarde.',
  },
  {
    id: 'vitesse-rapports',
    title: 'Montée de vitesse & rétrogradation',
    objectif: "L'élève coordonne le passage des rapports avec l'allure et la trajectoire.",
    pourquoi: 'Coordonner la relation vitesse → rapport → régime moteur pour accélérer et ralentir sans perdre la maîtrise.',
    comment: ['Anticiper le passage', 'Coordonner embrayage / accélération', 'Adapter le régime à l\u2019allure'],
    exercices: [
      { nom: 'Séquence 1→2→3→2→1', niveau: 2, description: 'Sur ligne droite, montée puis rétrogradation, arrêt.' },
      { nom: 'Intégration virage', niveau: 4, description: 'Insérer un virage entre les changements de rapport.' },
    ],
    observation: ['Regard qui plonge sur le compteur', 'Rétrogradation trop tôt', 'À-coup moteur', 'Perte de trajectoire'],
    risques: ['Rupture de motricité', 'Sur-régime', 'Perte d\u2019équilibre à basse vitesse'],
    influences: ['Type de moto', 'Déclivité', 'Charge'],
    erreurs: ['Regarder le compteur', 'Rétrograder sans avoir ralenti'],
    remediation: ['Faire les passages moto à l\u2019arrêt d\u2019abord (à vide)', 'Ligne droite avec repères visuels'],
    questions: ["«Où doit être ton regard pendant que tu changes de rapport ?»", "«Comment la moto te dit qu\u2019elle est prête à monter d\u2019un rapport ?»"],
    critere: 'L\u2019élève enchaîne montée et rétrogradation sans regarder le compteur, sans à-coup et sans perte de trajectoire.',
  },
  {
    id: 'arret-depart-tournant',
    title: 'Arrêt & départ en tournant',
    objectif: "L'élève enchaîne un arrêt propre puis un redémarrage roue braquée.",
    pourquoi: 'Compétence indispensable en circulation urbaine : feu, priorité, virage serré. Elle assemble équilibre + regard + direction + reprise de motricité.',
    comment: ['Arrêt stable, pieds au sol', 'Repositionner la moto si besoin', 'Regard sortie', 'Embrayage progressif', 'Frein arrière pour stabiliser'],
    exercices: [
      { nom: 'Arrêt-départ en virage large', niveau: 2, description: 'Arrivée lente → arrêt → regard sortie → départ en virage.' },
      { nom: 'Réduction du rayon disponible', niveau: 4, description: 'Diminuer progressivement l\u2019espace de sortie.' },
    ],
    observation: ['Regard qui décroche', 'Chute latérale au redémarrage', 'Sortie trop large'],
    risques: ['Chute au redémarrage', 'Trajectoire coupée'],
    influences: ['Inclinaison du sol', 'Braquage disponible', 'Charge'],
    erreurs: ['S\u2019arrêter braquage droit puis vouloir tourner', 'Ouvrir les gaz avant l\u2019équilibre'],
    remediation: ['Reprendre le patinage + frein arrière', 'Isoler le regard sortie moto arrêtée'],
    questions: ["«Où regardes-tu au moment où tu t'arrêtes ?»"],
    critere: 'L\u2019élève effectue 3 arrêts-départs en virage successifs sans poser le pied.',
  },
  {
    id: 'demi-tour',
    title: 'Demi-tour à basse vitesse',
    objectif: "L'élève réalise un demi-tour maîtrisé combinant préparation, prise d'angle, regard et remise progressive de motricité.",
    pourquoi: 'Manœuvre-clé de l\u2019usage urbain quotidien : combine tous les fondamentaux (regard, embrayage, freinage, inertie).',
    comment: ['Phase 1 — Préparation : allure adaptée + freins avant/arrière', 'Phase 2 — Prise d\u2019angle : regard sortie + débrayage complet + inertie', 'Phase 3 — Sortie : ré-embrayage progressif + gaz progressif'],
    exercices: [
      { nom: 'Demi-tour large', niveau: 1, description: 'Espace généreux, sans contrainte de temps.' },
      { nom: 'Demi-tour plus serré', niveau: 3, description: 'Espace réduit progressivement.' },
      { nom: 'Gauche puis droite', niveau: 3, description: 'Alternance pour identifier la dominance.' },
      { nom: 'Alternance G/D', niveau: 4, description: 'Enchaînement des deux côtés.' },
    ],
    observation: ['Arrivée trop rapide', 'Regard sur le cône', 'Embrayage relâché trop tôt', 'Remise de gaz brutale'],
    risques: ['Chute intérieure', 'Sortie trop large', 'Perte de motricité en sortie'],
    influences: ['Côté de réalisation', 'Type de moto', 'Pente', 'Charge'],
    erreurs: ['Freiner pendant la prise d\u2019angle', 'Accélérer trop tôt en sortie', 'Fixer le point le plus proche'],
    remediation: ['Reprendre la préparation moto à l\u2019arrêt', 'Refaire un demi-tour très large avec regard forcé vers la sortie'],
    questions: ["«À quel moment as-tu besoin de retrouver la motricité ?»", "«Qu\u2019est-ce qui change entre ton demi-tour à gauche et ton demi-tour à droite ?»"],
    critere: 'L\u2019élève réalise 3 demi-tours consécutifs (G et D) sans poser le pied.',
    aRetenir: 'Je freine pour préparer → je débraye pour laisser vivre la moto → je regarde la sortie → je ré-embraye progressivement pour repartir.',
  },
  {
    id: 'contre-braquage',
    title: 'Contre-braquage',
    objectif: "L'élève comprend et met en œuvre le contre-braquage comme une coordination globale du corps sur la moto.",
    pourquoi: "À partir d'une certaine vitesse, la moto se dirige par contre-braquage. Comprendre le fonctionnement plutôt que reproduire un mouvement.",
    comment: ['Guidon : action progressive', 'Repose-pieds & jambes stables', 'Bassin en appui', 'Regard vers la sortie'],
    exercices: [
      { nom: 'Changement de direction G/D à vitesse modérée', niveau: 2, description: 'Sentir l\u2019effet de l\u2019action guidon.' },
      { nom: 'Slalom espacé', niveau: 3, description: 'Enchaîner des inclinaisons.' },
    ],
    observation: ['Crispation guidon', 'Trajectoire large', 'Regard fixe sur l\u2019obstacle'],
    risques: ['Perte de contrôle sur cône', 'Trajectoire élargie'],
    influences: ['Vitesse', 'Adhérence', 'Pneus', 'Vent'],
    erreurs: ['Résumer le contre-braquage à «pousser le guidon»', 'Se crisper', 'Regarder les cônes'],
    remediation: ['Reprendre à allure modérée avec espacement plus large', 'Faire ressentir l\u2019appui repose-pieds moto à l\u2019arrêt'],
    questions: ["«Où regardes-tu lorsque tu veux que la moto change de direction ?»", "«Quel rôle jouent tes jambes ?»"],
    critere: 'L\u2019élève enchaîne 4 changements de direction sans se crisper, avec regard clairement porté vers la sortie.',
    aRetenir: 'La maîtrise de la trajectoire vient du corps entier, pas seulement des mains.',
  },
  {
    id: 'freinage-urgence',
    title: 'Freinage d\u2019urgence',
    objectif: "L'élève réagit face à un danger imprévu en conservant la maîtrise de sa moto.",
    pourquoi: "L'urgence ne doit pas créer de précipitation. Elle doit déclencher une séquence maîtrisée : voir → redresser → freiner → contrôler.",
    comment: ['Redresser la moto', 'Fermer les gaz', 'Utiliser les 2 freins avec pression progressive', 'Maintenir la trajectoire', 'Immobiliser la moto'],
    exercices: [
      { nom: 'Freinage à faible allure', niveau: 1, description: 'Sentir la puissance de chaque frein.' },
      { nom: 'Séquence complète à allure croissante', niveau: 3, description: 'Accélération → repère → fermeture des gaz → freinage → arrêt.' },
    ],
    observation: ['Freinage brutal', 'Blocage d\u2019une roue', 'Regard sur l\u2019obstacle', 'Moto pas assez redressée'],
    risques: ['Blocage roue', 'Chute par déséquilibre', 'Choc arrière'],
    influences: ['Vitesse', 'Adhérence', 'Pneus', 'Temps de réaction', 'Charge'],
    erreurs: ['Serrer le frein avant d\u2019un coup', 'Freiner sans redresser', 'Regarder l\u2019obstacle'],
    remediation: ['Reprendre la répartition du freinage sur ligne droite', 'Isoler la fermeture des gaz avant le freinage'],
    questions: ["«Quand as-tu identifié le danger ?»", "«Où regardais-tu au moment de freiner ?»"],
    critere: 'L\u2019élève immobilise la moto dans la distance attendue, sans blocage de roue, regard maintenu loin.',
    aRetenir: 'Je vois, je redresse, je freine, je contrôle.',
  },
  {
    id: 'evitement',
    title: 'Évitement',
    objectif: "L'élève identifie qu'une situation dangereuse peut nécessiter d'éviter, de freiner, ou de combiner les deux.",
    pourquoi: 'Freiner n\u2019est pas toujours la bonne réponse. L\u2019évitement introduit une autre solution face à un danger.',
    comment: ['Identifier l\u2019obstacle', 'Regard vers la zone de sortie', 'Déclencher le contre-braquage', 'Revenir progressivement dans l\u2019axe'],
    exercices: [
      { nom: 'Évitement à faible allure', niveau: 2, description: 'Ressentir la logique regard + contre-braquage.' },
      { nom: 'Évitement à droite puis à gauche', niveau: 3, description: 'Alterner les deux sens.' },
    ],
    observation: ['Fixation de l\u2019obstacle', 'Freinage pendant l\u2019évitement', 'Mouvement brutal du guidon'],
    risques: ['Perte de trajectoire', 'Chute par mouvement brutal'],
    influences: ['Vitesse', 'Distance disponible', 'Regard', 'Adhérence'],
    erreurs: ['Vouloir freiner et éviter en même temps', 'Fixer l\u2019obstacle des yeux'],
    remediation: ['Reprendre à allure faible sans freinage associé', 'Décomposer regard → contre-braquage → retour axe'],
    questions: ["«Si tu regardes l\u2019obstacle, où risques-tu d\u2019aller ?»"],
    critere: 'L\u2019élève évite l\u2019obstacle sans le toucher, en gardant sa trajectoire de sortie sous contrôle.',
    aRetenir: 'Je regarde où je veux aller, pas l\u2019obstacle que je veux éviter.',
  },
  {
    id: 'passager',
    title: 'Conduite avec passager',
    objectif: "L'élève adapte sa conduite au comportement modifié de la moto avec passager et communique avec lui.",
    pourquoi: 'Le passager modifie le comportement de la moto : poids, équilibre, inertie, réactions.',
    comment: ['Expliquer la position au passager', 'Code de communication', 'Démarrage progressif', 'Frein arrière adapté', 'Arrêt progressif'],
    exercices: [
      { nom: 'Cercle large avec passager', niveau: 2, description: 'Se familiariser avec le nouveau comportement.' },
      { nom: 'Départ + virage + arrêt', niveau: 3, description: 'Introduire les phases essentielles.' },
    ],
    observation: ['Déstabilisation au démarrage', 'Passager qui accompagne mal', 'Arrêt brutal'],
    risques: ['Chute au démarrage', 'Trajectoire élargie'],
    influences: ['Poids du passager', 'Taille', 'Comportement'],
    erreurs: ['Sous-estimer l\u2019inertie ajoutée', 'Communiquer trop peu avec le passager'],
    remediation: ['Refaire la préparation moto à l\u2019arrêt', 'Reprendre le cercle sans passager avant de le réintégrer'],
    questions: ["«Qu\u2019as-tu ressenti par rapport au même trajet sans passager ?»", "«Comment avez-vous communiqué ?»"],
    critere: 'L\u2019élève démarre, tourne et s\u2019arrête avec passager sans le déstabiliser, avec communication établie.',
  },
];

/* ================================================================== */
/*  PHASE 3 — EN CIRCULATION (~3 h)                                    */
/* ================================================================== */
const P3_CIRCULATION = [
  {
    id: 'theorie-route',
    title: 'Théorie de la route & position sur la chaussée',
    objectif: "Le conducteur se place correctement pour optimiser sa visibilité, sa trajectoire et sa marge de sécurité.",
    pourquoi: 'La position sur la voie est un des premiers déterminants de la sécurité et du confort de conduite.',
    comment: ['Position dans la voie (tiers gauche par défaut)', 'Adaptation selon les situations', 'Distances de sécurité', 'Prises d\u2019information'],
    exercices: [
      { nom: 'Circulation en zone tranquille', niveau: 2, description: 'Vérifier position et adaptation.' },
      { nom: 'Trajet urbain à densité modérée', niveau: 4, description: 'Adapter la position selon les configurations.' },
    ],
    observation: ['Position trop à droite («coller le trottoir»)', 'Position trop centrale sans raison', 'Ne pas voir / ne pas être vu'],
    risques: ['Être invisible', 'Manquer de marge en cas de danger latéral'],
    influences: ['Type de voie', 'Trafic', 'Météo'],
    erreurs: ['Se placer «pour ne pas gêner»', 'Rester statique quelle que soit la configuration'],
    remediation: ['Verbaliser à voix haute sa position et pourquoi', 'Reprise sur zone plus calme'],
    questions: ["«Pourquoi choisis-tu ce placement ici ?»", "«Que se passerait-il si tu roulais 1 m plus à droite ?»"],
    critere: 'Le conducteur justifie son placement selon la voie et l\u2019adapte spontanément aux configurations rencontrées.',
  },
  {
    id: 'changement-direction',
    title: 'Changement de direction',
    objectif: "Le conducteur anticipe, informe, contrôle et exécute un changement de direction en sécurité.",
    pourquoi: 'Chaque changement doit être préparé et sécurisé. Un oubli d\u2019angle mort peut être fatal.',
    comment: ['Contrôle rétroviseur', 'Angle mort', 'Clignotant', 'Positionnement', 'Action', 'Rétablissement'],
    exercices: [
      { nom: 'Séquence complète', niveau: 3, description: 'Rétro → angle mort → cligno → positionnement → action → rétablissement.' },
      { nom: 'Enchaînement de 3 changements', niveau: 4, description: 'Trajet urbain avec plusieurs changements.' },
    ],
    observation: ['Oubli du contrôle angle mort', 'Clignotant tardif', 'Trajectoire coupée'],
    risques: ['Collision latérale avec véhicule non vu', 'Trajectoire dangereuse'],
    influences: ['Vitesse', 'Densité de trafic', 'Visibilité'],
    erreurs: ['Contrôler uniquement le rétroviseur sans regarder l\u2019angle mort', 'Actionner le clignotant après la manœuvre'],
    remediation: ['Reprise à l\u2019arrêt de la séquence complète', 'Verbalisation de chaque étape'],
    questions: ["«À quel moment as-tu regardé ton angle mort ?»", "«Qu\u2019as-tu vu en tournant la tête ?»"],
    critere: 'Le conducteur réalise 3 changements de direction consécutifs avec toutes les étapes présentes et dans l\u2019ordre.',
  },
  {
    id: 'intersections',
    title: 'Franchissement des intersections',
    objectif: "Le conducteur aborde les intersections en réduisant le risque de refus de priorité subi.",
    pourquoi: 'Les intersections concentrent une part importante des accidents 2-roues. La priorité doit se vérifier, jamais se supposer.',
    comment: ['Réduire l\u2019allure à l\u2019approche', 'Voir des 2 côtés', 'Se rendre visible', 'Franchir avec une allure adaptée', 'Reprendre progressivement'],
    exercices: [
      { nom: 'Approche stop / cédez le passage', niveau: 2, description: 'Décomposer prise d\u2019information et engagement.' },
      { nom: 'Traversée d\u2019intersection à visibilité réduite', niveau: 4, description: 'Adaptation de l\u2019allure et positionnement.' },
    ],
    observation: ['Vitesse d\u2019approche trop élevée', 'Prise d\u2019information tardive', 'Position peu visible'],
    risques: ['Refus de priorité subi', 'Angle mort d\u2019un véhicule qui tourne'],
    influences: ['Type d\u2019intersection', 'Visibilité', 'Trafic'],
    erreurs: ['Franchir «parce qu\u2019on a la priorité»', 'Regarder uniquement à gauche'],
    remediation: ['Reprise sur intersection connue', 'Verbalisation des prises d\u2019information'],
    questions: ["«Comment sais-tu que l\u2019automobiliste t\u2019a vu ?»", "«Que ferais-tu s\u2019il te forçait la priorité ?»"],
    critere: 'Le conducteur adapte son allure et ses prises d\u2019information à chaque intersection, sans jamais supposer sa priorité.',
  },
  {
    id: 'giratoires',
    title: 'Giratoires',
    objectif: "Le conducteur négocie un giratoire en gérant insertion, trajectoire et sortie.",
    pourquoi: 'Le giratoire combine changement de direction, insertion, gestion de la trajectoire et sortie sécurisée.',
    comment: ['Approche adaptée', 'Prise d\u2019information', 'Insertion progressive', 'Trajectoire fluide', 'Clignotant de sortie'],
    exercices: [
      { nom: 'Giratoire simple 1 voie', niveau: 2, description: 'Négocier entrée, tour, sortie.' },
      { nom: 'Giratoire 2 voies', niveau: 4, description: 'Choisir la voie selon la sortie prévue.' },
    ],
    observation: ['Insertion trop brusque', 'Trajectoire coupée par un autre usager', 'Sortie non signalée'],
    risques: ['Collision à l\u2019insertion', 'Chute par trajectoire tendue'],
    influences: ['Taille du giratoire', 'Densité', 'Nombre de voies'],
    erreurs: ['Rentrer sans céder', 'Oublier le clignotant de sortie', 'Trajectoire tendue en sortie'],
    remediation: ['Reprise sur giratoire calme', 'Verbalisation entrée / tour / sortie'],
    questions: ["«À quel moment mets-tu ton clignotant de sortie ?»"],
    critere: 'Le conducteur négocie 3 giratoires consécutifs sans hésitation, avec clignotant de sortie systématique.',
  },
  {
    id: 'virages',
    title: 'Virages',
    objectif: "Le conducteur négocie un virage en respectant les 4 étapes : Voir → Placer → Tourner → Sortir.",
    pourquoi: 'Une erreur en entrée conditionne toute la manœuvre. Le regard doit avoir une étape d\u2019avance.',
    comment: ['Voir → Placer → Tourner → Sortir', 'Vitesse adaptée avant le virage', 'Regard vers la sortie', 'Position stable'],
    exercices: [
      { nom: 'Route avec courbes progressives', niveau: 3, description: 'Appliquer les 4 étapes.' },
      { nom: 'Route avec épingles', niveau: 5, description: 'Adaptation forte de la vitesse d\u2019entrée.' },
    ],
    observation: ['Vitesse d\u2019entrée trop élevée', 'Freinage en virage', 'Trajectoire large en sortie'],
    risques: ['Sortie de route', 'Franchissement de ligne'],
    influences: ['Rayon', 'Adhérence', 'Visibilité', 'Charge'],
    erreurs: ['Freiner dans le virage plutôt qu\u2019avant', 'Rentrer trop vite «pour compenser»'],
    remediation: ['Reprise sur virages plus larges', 'Verbalisation des 4 étapes'],
    questions: ["«Où étaient tes yeux à l\u2019entrée du virage ?»", "«Qu\u2019est-ce qui t\u2019a fait accélérer / ralentir ?»"],
    critere: 'Le conducteur enchaîne 5 virages consécutifs en appliquant les 4 étapes, sans freinage en virage.',
  },
  {
    id: 'autoroute-depart',
    title: 'Autoroute & départ sur route',
    objectif: "Le conducteur aborde les vitesses plus élevées en gérant vent, dépassements, distances et fatigue.",
    pourquoi: 'Les vitesses élevées modifient les distances, la fatigue et le comportement moto/pilote.',
    comment: ['Insertion progressive', 'Distances de sécurité étendues', 'Gestion du vent latéral', 'Contrôle rétroviseurs fréquent', 'Sortie anticipée'],
    exercices: [
      { nom: 'Insertion voie rapide', niveau: 4, description: 'Prendre de la vitesse sur la voie d\u2019accélération.' },
      { nom: 'Trajet 10 min à 110 km/h', niveau: 5, description: 'Maintenir position, distance, vigilance.' },
    ],
    observation: ['Sous-estimation de la fatigue', 'Distance insuffisante', 'Angle mort ignoré'],
    risques: ['Choc arrière', 'Sortie de route sur bourrasque', 'Ralentissement soudain non anticipé'],
    influences: ['Vent', 'Météo', 'État route', 'Fatigue'],
    erreurs: ['Rouler à 2 s au lieu de 4 s à vitesse élevée', 'Utiliser le régulateur mental sans vérifier'],
    remediation: ['Reprise sur voie moins chargée', 'Verbalisation des vérifications fréquentes'],
    questions: ["«Comment aurais-tu réagi si la voiture devant s\u2019était arrêtée d\u2019un coup ?»"],
    critere: 'Le conducteur soutient l\u2019allure sans stress, avec distances larges, vérifications régulières et sortie préparée.',
  },
];

export const PHASES_125 = [
  { id: 'theorie',        code: '1', title: 'Théorie',                     subtitle: 'Comprendre les enjeux, identifier les risques',       duration: '≈ 1 h à 1 h 30',    color: '#0891b2', context: 'salle',       accroche: "La théorie s'adapte à l'expérience du conducteur. Elle peut être réduite pour privilégier la pratique lorsque les connaissances sont déjà solides.", exercises: P1_THEORIE },
  { id: 'hors-circulation',code: '2', title: 'Formation hors circulation', subtitle: 'Acquérir les bases techniques avant de circuler',      duration: '≈ 2 h 30 à 3 h',    color: '#16a34a', context: 'plateau',     accroche: 'On construit les compétences techniques du plus simple au plus complexe. Chaque exercice est adapté au niveau réel du conducteur.',     exercises: P2_HORS_CIRC },
  { id: 'circulation',    code: '3', title: 'Formation en circulation',    subtitle: "Mettre en pratique en situation réelle — si le niveau le permet", duration: '≈ 3 h',        color: '#ea580c', context: 'circulation', accroche: "La circulation intervient lorsque le niveau technique de l'élève permet de l'aborder en sécurité. Si nécessaire, les points faibles sont retravaillés hors circulation.", exercises: P3_CIRCULATION },
];

export const RAPPEL_CIRCULATION = "La circulation n'est pas automatique : elle est réalisée uniquement si le niveau technique de l'élève est suffisant. Si nécessaire, les points faibles sont retravaillés hors circulation avant de partir sur route.";

export function buildSynthese125() {
  const lines = [];
  lines.push('# Formation 125 (7 h) — Progression pédagogique CODE 2 ROUES');
  lines.push('');
  lines.push(`**Méthode :** ${INTRO_125.baseline}`);
  lines.push('');
  lines.push(`> ${INTRO_125.principe}`);
  lines.push('');
  PHASES_125.forEach(phase => {
    lines.push(`## ${phase.code}. ${phase.title.toUpperCase()} — ${phase.duration}`);
    lines.push(`*${phase.subtitle}*`);
    lines.push('');
    lines.push(phase.accroche);
    lines.push('');
    phase.exercises.forEach(ex => {
      lines.push(`### ${ex.title}`);
      lines.push(`**🎯 Objectif :** ${ex.objectif}`);
      lines.push(`**Pourquoi ?** ${ex.pourquoi}`);
      lines.push('**Comment ?**');
      ex.comment.forEach(c => lines.push(`- ${c}`));
      lines.push('**🏍️ Exercices :**');
      ex.exercices.forEach(e => lines.push(`- **${e.nom}** _(niv. ${e.niveau})_ : ${e.description}`));
      lines.push('**👀 Observation :** ' + ex.observation.join(' · '));
      lines.push('**⚠️ Risques :** ' + ex.risques.join(' · '));
      lines.push('**🌦️ Influences :** ' + ex.influences.join(' · '));
      lines.push('**Erreurs fréquentes :** ' + ex.erreurs.join(' · '));
      lines.push('**🔧 Remédiation :** ' + ex.remediation.join(' · '));
      lines.push('**🗣️ Questions :**');
      ex.questions.forEach(q => lines.push(`- ${q}`));
      lines.push(`**✅ Critère de réussite :** ${ex.critere}`);
      if (ex.aRetenir) lines.push(`> **À retenir :** ${ex.aRetenir}`);
      lines.push('');
    });
  });
  lines.push('---');
  lines.push(`*${RAPPEL_CIRCULATION}*`);
  return lines.join('\n');
}
